import classNames from "classnames";
import * as jdenticon from "jdenticon";
import { Avatar } from "primereact/avatar";
import { MenuItem } from "primereact/menuitem";
import { TieredMenu } from "primereact/tieredmenu";
import { Tooltip } from "primereact/tooltip";
import { RefObject, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import * as Token from "../../api/auth_token";
import {
  featureEnabled,
  isPreviewAsUserEnabled,
  PREVIEW_AS_USER_EMAIL,
  setPreviewAsUserEnabled,
} from "../../client-features";
import { useClientId } from "../../ClientConfig";
import {
  fakeAssigneeNameForAssignee,
  usePrivacyMode,
} from "../../context-providers/Privacy";
import { IfFeatureEnabled } from "../../IfFeatureEnabled";
import { useLocalUser } from "../../pages/specialty-assistant/data";
import { useMapModal } from "../../pages/specialty-assistant/map/MapModalContext";
import { useIsMobile } from "../../responsive/useMediaQuery";
import { ROUTES } from "../../routes";
import { ThemeContext, ThemeMode } from "../../themes/Theme";
import { sleepMs } from "../../util";
import { BdxPerilBreakdownModal } from "../bdx/BdxPerilBreakdownModal";
import { ExportReportModal } from "../export-report/ExportReportModal";
import { PortfolioGateDialog } from "../../pages/specialty-assistant/layers/PortfolioGateDialog";
import styles from "./Avatar.module.css";

type PantheonAvatarProps = {
  /* If label is undefined, a question mark for "unassigned" will be displayed */
  label?: string;
  /* If provided, used as the avatar image instead of the generated identicon */
  imageUrl: string | null | undefined;
  size?: "small" | "large";
  withMenu?: boolean;
  showName?: boolean;
};

// It's not enough to useMemo these because many use cases for Avatars get
// unmounted and remounted frequently, and these URls take several ms to
// generate.
const DATA_URL_CACHE: { [val: string]: string | undefined } = {};
export const generateIdenticonDataUrl = (val: string): string | undefined => {
  if (!DATA_URL_CACHE[val]) {
    // Try changes at https://jdenticon.com/icon-designer.html?config=ffffffff10c32843234c1840
    const jdenticonConfig = {
      hues: [195],
      lightness: {
        color: [0.35, 0.77],
        grayscale: [0.25, 0.65],
      },
      saturation: {
        color: 0.4,
        grayscale: 0.68,
      },
      backColor: "#fff",
    };
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      jdenticon.drawIcon(ctx, val, 64, jdenticonConfig);
      DATA_URL_CACHE[val] = canvas.toDataURL();
    } else {
      DATA_URL_CACHE[val] = undefined;
    }
  }
  return DATA_URL_CACHE[val];
};

const useGenerateIdenticonDataUrl = (val: string | undefined) => {
  const [lastValAndComputed, setLastValAndComputed] = useState<
    [string | undefined, string | undefined] | undefined
  >(undefined);
  useEffect(() => {
    if (!val) {
      setLastValAndComputed([val, undefined]);
    } else if (val && val in DATA_URL_CACHE) {
      setLastValAndComputed([val, DATA_URL_CACHE[val]]);
    } else if (val && lastValAndComputed?.[0] !== val) {
      // defer after this render, kinda hacky. Long term icon generation should be put in a webworker or done better somehow.
      sleepMs(0)
        .then(() => generateIdenticonDataUrl(val))
        .then((computed) => setLastValAndComputed([val, computed]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [val]);

  return lastValAndComputed?.[1];
};

const AvatarBase: React.FC<
  PantheonAvatarProps & { menuRef?: RefObject<TieredMenu> }
> = ({ label: rawLabel, imageUrl, size, showName, menuRef, withMenu }) => {
  const { privacyModeEnabled } = usePrivacyMode();
  const label =
    privacyModeEnabled && rawLabel
      ? fakeAssigneeNameForAssignee(rawLabel)
      : rawLabel;
  const tooltipId = useRef(`tooltip-${uuid()}`);
  const nameToShow = label ?? "Unassigned";
  // Don't show the real profile photo in privacy mode.
  const identicon = useGenerateIdenticonDataUrl(label);
  const hasRealImage = !privacyModeEnabled && !!imageUrl;
  const image = hasRealImage ? imageUrl : identicon;
  // Real photos and the "unassigned" placeholder render as rounded squares.
  // Identicons stay circular so named-but-photo-less users keep the old look.
  const useRoundedSquare = hasRealImage || !label;

  return (
    <>
      {!showName && (
        <Tooltip target={`#${tooltipId.current}`} position="left" />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Avatar
          shape={useRoundedSquare ? "square" : "circle"}
          className={classNames(
            styles.avatar,
            size === "small" ? styles.avatarSmall : styles.avatarLarge,
            useRoundedSquare && styles.avatarRoundedSquare
          )}
          style={{ cursor: withMenu ? "pointer" : "default" }}
          icon="pi pi-question-circle"
          image={image ?? undefined}
          data-pr-tooltip={nameToShow}
          data-pr-showdelay={500}
          onClick={(e) => menuRef?.current?.toggle(e)}
          id={tooltipId.current}
        />
        {showName && (
          <span
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              color: "var(--text-color)",
            }}
          >
            {nameToShow}
          </span>
        )}
      </div>
    </>
  );
};

export const PantheonAvatarWithMenu: React.FC<PantheonAvatarProps> = ({
  label,
  imageUrl,
  size = "small",
  showName = false,
}) => {
  const clientId = useClientId();
  const ref = useRef<TieredMenu>(null);
  const { showMapModal } = useMapModal();
  const localUser = useLocalUser();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { mode: darkMode, setThemeMode: setDarkMode } =
    useContext(ThemeContext);
  const [showExportReportModal, setShowExportReportModal] = useState(false);
  const [showBdxPerilBreakdownModal, setShowBdxPerilBreakdownModal] =
    useState(false);
  const [showPortfolioGates, setShowPortfolioGates] = useState(false);

  const { privacyModeEnabled, togglePrivacyMode } = usePrivacyMode();
  const previewingAsUser = isPreviewAsUserEnabled();
  const darkModeOptions: [ThemeMode, string][] = [
    ["light", "Always light"],
    ["dark", "Always dark"],
    ["auto", "System"],
  ];

  const isInternalUser =
    localUser?.email === "pantheon_test_user" ||
    localUser?.email.endsWith("@pantheonai.co");

  type MenuEntry = MenuItem & { hideOnMobile?: boolean };
  const menuEntries: (MenuEntry | undefined)[] = [
    {
      disabled: true,
      style: { opacity: "1" },
      template: () => (
        <div className={styles.avatarInMenuContainer}>
          <AvatarBase
            label={label}
            imageUrl={imageUrl}
            size="small"
            showName={true}
            withMenu={false}
          />
        </div>
      ),
    },
    { separator: true },
    featureEnabled(clientId, "analytics-page", { user: localUser })
      ? {
          label: "Analytics",
          icon: "pi pi-chart-line",
          command: () => navigate(ROUTES.DASHBOARD_ANALYTICS),
          hideOnMobile: true,
        }
      : undefined,
    featureEnabled(clientId, "exposure-map-page", { user: localUser })
      ? {
          label: "Exposure Map",
          icon: "pi pi-globe",
          command: () => navigate(ROUTES.DASHBOARD_EXPOSURE_MAP),
          hideOnMobile: true,
        }
      : undefined,
    isInternalUser
      ? {
          label: "Search Bound Policies",
          icon: "pi pi-map",
          command: showMapModal,
        }
      : undefined,
    featureEnabled(clientId, "export-report", { user: localUser })
      ? {
          label: "Export Report",
          icon: "pi pi-file-excel",
          command: () => setShowExportReportModal(true),
          hideOnMobile: true,
        }
      : undefined,
    featureEnabled(clientId, "bdx-peril-breakdown", { user: localUser })
      ? {
          label: "BDX Peril Breakdown",
          icon: "pi pi-table",
          command: () => setShowBdxPerilBreakdownModal(true),
          hideOnMobile: true,
        }
      : undefined,
    featureEnabled(clientId, "brokerage-analytics", { user: localUser })
      ? {
          label: "Brokerage Analytics",
          icon: "pi pi-chart-bar",
          command: () => navigate(ROUTES.BROKER_ANALYTICS),
          hideOnMobile: true,
        }
      : undefined,
    featureEnabled(clientId, "submission-history", { user: localUser })
      ? {
          label: "Submission History",
          icon: "pi pi-history",
          command: () => navigate(ROUTES.DASHBOARD_SUBMISSION_HISTORY),
          hideOnMobile: true,
        }
      : undefined,
    {
      label: "Gate Accumulation",
      icon: "pi pi-chart-bar",
      command: () => setShowPortfolioGates(true),
      hideOnMobile: true,
    },
    // Nested submenus open off the right edge on phones, so flatten the dark
    // mode options into top-level items there.
    ...(isMobile
      ? [
          {
            label: "Dark mode",
            disabled: true,
            style: { opacity: "1" },
          },
          ...darkModeOptions.map(([value, label]) => ({
            label,
            icon: darkMode === value ? "pi pi-check-circle" : "pi pi-circle",
            command: () => setDarkMode(value),
          })),
        ]
      : [
          {
            label: "Dark mode",
            icon: "pi pi-moon",
            items: darkModeOptions.map(([value, label]) => ({
              label,
              icon: darkMode === value ? "pi pi-check-circle" : "pi pi-circle",
              command: () => setDarkMode(value),
            })),
          },
        ]),
    {
      label: "Sign out",
      icon: "pi pi-sign-out",
      command: () => {
        Token.logOut();
        window.location.reload();
      },
    },
    isInternalUser
      ? {
          label: "Privacy Mode",
          icon: privacyModeEnabled ? "pi pi-eye" : "pi pi-eye-slash",
          command: () => {
            togglePrivacyMode();
          },
        }
      : undefined,
    // Gated on the real signed-in user rather than a feature. An
    // enable_for_admin feature would resolve against the previewed non-admin
    // and hide this entry. An active preview keeps the entry too, so losing
    // admin rights partway through cannot strand anyone in it.
    localUser?.is_client_admin || previewingAsUser
      ? {
          label: previewingAsUser ? `Exit preview as UW` : `Preview as UW`,
          icon: previewingAsUser ? "pi pi-user-minus" : "pi pi-user-plus",
          command: () => {
            setPreviewAsUserEnabled(!previewingAsUser);
            window.location.reload();
          },
        }
      : undefined,
  ];

  const allMenuItems = menuEntries
    .filter((x): x is MenuEntry => !!x)
    .filter((x) => !(isMobile && x.hideOnMobile));

  return (
    <>
      <IfFeatureEnabled feature="export-report" user={localUser}>
        {showExportReportModal && (
          <ExportReportModal
            visible={showExportReportModal}
            setVisible={setShowExportReportModal}
          />
        )}
      </IfFeatureEnabled>
      <IfFeatureEnabled feature="bdx-peril-breakdown" user={localUser}>
        {showBdxPerilBreakdownModal && (
          <BdxPerilBreakdownModal
            visible={showBdxPerilBreakdownModal}
            setVisible={setShowBdxPerilBreakdownModal}
          />
        )}
      </IfFeatureEnabled>
      <PortfolioGateDialog
        visible={showPortfolioGates}
        onHide={() => setShowPortfolioGates(false)}
      />
      <TieredMenu
        model={allMenuItems}
        popup
        ref={ref}
        style={{ minWidth: "232px" }}
      />
      <div className={styles.avatarWithBadge}>
        {previewingAsUser && (
          <span className={styles.previewAsUserBadge}>
            Previewing as {PREVIEW_AS_USER_EMAIL}
          </span>
        )}
        <AvatarBase
          label={label}
          imageUrl={imageUrl}
          size={size}
          showName={showName}
          menuRef={ref}
          withMenu
        />
      </div>
    </>
  );
};

export const PantheonAvatarWithoutMenu: React.FC<PantheonAvatarProps> = ({
  label,
  imageUrl,
  size = "small",
  showName = false,
}) => (
  <AvatarBase
    label={label}
    imageUrl={imageUrl}
    size={size}
    showName={showName}
    withMenu={false}
  />
);
